/**
 * /recruitment/approvals + /me/recruitment/approvals — the recruitment
 * pending-approval queue (ADR-0012 Phase 2.1). Dual-mounted: under the admin
 * layout (HR) and the employee layout (a non-HR resolved manager — the same
 * sticky-approver pattern as overtime). The API self-filters by the caller's
 * approver id, so a non-approver simply sees an empty queue.
 *
 *   "Pending my approval" — requisition-opens + offer-extends awaiting the caller.
 *   "All pending"         — every pending item (owner/admin/hr only).
 *
 * Each item: Approve, or Reject with a reason. Mirrors overtimeApprovals.
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { meLayoutRoute } from './meLayout.js';
import { useAuth } from '../auth/AuthContext.js';
import {
  approveExtendOffer,
  approveOpenRequisition,
  listApprovals,
  rejectExtendOffer,
  rejectOpenRequisition,
  type ApprovalsResponse,
} from '../api/recruitment.js';
import { ApiErrorNotice } from '../components/ApiErrorNotice.js';
import { formatMoney } from '../lib/format.js';
import type { CurrencyCode } from '@kora/types';

export const recruitmentApprovalsRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/approvals',
  component: RecruitmentApprovalsPage,
});
export const meRecruitmentApprovalsRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/recruitment/approvals',
  component: RecruitmentApprovalsPage,
});

type View = 'pending_my_approval' | 'all';

function RecruitmentApprovalsPage(): JSX.Element {
  const { user } = useAuth();
  const isHr = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr';
  const [view, setView] = useState<View>('pending_my_approval');
  const queryClient = useQueryClient();

  const query = useQuery({ queryKey: ['recruitment', 'approvals', view], queryFn: () => listApprovals(view) });
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['recruitment', 'approvals'] });

  const data: ApprovalsResponse | undefined = query.data;
  const empty = data && data.requisitionOpens.length === 0 && data.offerExtends.length === 0;

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Recruitment approvals</h1>
        <p className="text-sm text-gray-400">
          Opening a requisition and extending an offer both need the resolved
          manager’s sign-off. Approve to let it proceed, or reject with a reason.
        </p>
      </header>

      <nav className="flex gap-2 border-b border-gray-900">
        <TabButton active={view === 'pending_my_approval'} onClick={() => setView('pending_my_approval')}>
          Pending my approval
        </TabButton>
        {isHr ? (
          <TabButton active={view === 'all'} onClick={() => setView('all')}>
            All pending
          </TabButton>
        ) : null}
      </nav>

      {query.isLoading ? (
        <Placeholder>Loading…</Placeholder>
      ) : query.isError ? (
        <Placeholder error>Could not load the approval queue.</Placeholder>
      ) : empty ? (
        <Placeholder>Nothing pending right now.</Placeholder>
      ) : (
        <div className="space-y-6">
          {data!.requisitionOpens.length > 0 ? (
            <div className="space-y-3">
              <h2 className="text-sm uppercase tracking-wide text-gray-500">Requisitions awaiting open</h2>
              {data!.requisitionOpens.map((r) => (
                <ApprovalCard
                  key={r.id}
                  title={r.title}
                  subtitle={`${r.headcount} headcount${r.department ? ` · ${r.department}` : ''}`}
                  approveFn={() => approveOpenRequisition(r.id)}
                  rejectFn={(reason) => rejectOpenRequisition(r.id, { reason })}
                  onDecided={() => void refresh()}
                />
              ))}
            </div>
          ) : null}

          {data!.offerExtends.length > 0 ? (
            <div className="space-y-3">
              <h2 className="text-sm uppercase tracking-wide text-gray-500">Offers awaiting extend</h2>
              {data!.offerExtends.map((o) => (
                <ApprovalCard
                  key={o.id}
                  title={`${o.application.candidate.firstName} ${o.application.candidate.lastName}`}
                  subtitle={`${o.application.requisition.title} · ${formatMoney(o.grossSalary, o.currency as CurrencyCode)} / ${o.payFrequency}`}
                  approveFn={() => approveExtendOffer(o.id)}
                  rejectFn={(reason) => rejectExtendOffer(o.id, { reason })}
                  onDecided={() => void refresh()}
                />
              ))}
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

function ApprovalCard({
  title,
  subtitle,
  approveFn,
  rejectFn,
  onDecided,
}: {
  title: string;
  subtitle: string;
  approveFn: () => Promise<unknown>;
  rejectFn: (reason: string) => Promise<unknown>;
  onDecided: () => void;
}): JSX.Element {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const approveMut = useMutation({ mutationFn: approveFn, onSuccess: () => onDecided() });
  const rejectMut = useMutation({ mutationFn: () => rejectFn(reason), onSuccess: () => onDecided() });

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3">
      <header>
        <p className="text-sm text-white">{title}</p>
        <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
      </header>
      {rejecting ? (
        <div className="space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (optional)"
            aria-label="Rejection reason"
            className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          />
          <div className="flex gap-2">
            <button type="button" onClick={() => rejectMut.mutate()} disabled={rejectMut.isPending} className="rounded bg-red-700 text-white text-xs px-3 py-1 disabled:opacity-50">
              {rejectMut.isPending ? 'Rejecting…' : 'Confirm reject'}
            </button>
            <button type="button" onClick={() => { setRejecting(false); setReason(''); }} className="rounded border border-gray-800 text-xs px-3 py-1 text-gray-300">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button type="button" onClick={() => approveMut.mutate()} disabled={approveMut.isPending} className="rounded bg-green-700 text-white text-xs px-3 py-1 disabled:opacity-50">
            {approveMut.isPending ? 'Approving…' : 'Approve'}
          </button>
          <button type="button" onClick={() => setRejecting(true)} className="rounded border border-gray-700 text-xs px-3 py-1 hover:bg-gray-900">
            Reject
          </button>
        </div>
      )}
      <ApiErrorNotice error={approveMut.error ?? rejectMut.error} fallback="Action failed." compact />
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }): JSX.Element {
  return (
    <button type="button" onClick={onClick} className={`px-3 py-2 text-sm border-b-2 ${active ? 'border-white text-white' : 'border-transparent text-gray-400 hover:text-white'}`}>
      {children}
    </button>
  );
}

function Placeholder({ children, error }: { children: React.ReactNode; error?: boolean }): JSX.Element {
  return (
    <div className={`rounded border p-6 text-center text-sm ${error ? 'border-red-900 bg-red-950/40 text-red-300' : 'border-gray-900 text-gray-500'}`}>
      {children}
    </div>
  );
}
